import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { getIdToken } from '@/providers/auth-provider';
import { toast } from '@/hooks/useToast';
import { Gift, ChevronLeft, ChevronRight, Check } from 'lucide-react';

type Source = 'tester' | 'marketing' | 'referral' | 'support' | 'vip' | 'other';
type IssuanceType = 'admin_grant' | 'shareable_code' | 'unique_codes' | 'referral_auto';
type ReferralMilestone = 'signup' | 'profile_complete' | 'first_match' | 'paid';

interface FormState {
  // Step 1
  name: string;
  description: string;
  source: Source;
  paywall_type: string;
  // Step 2
  coupons_per_claim: number;
  max_claims_per_user: number;
  max_total_claims: number | '';
  starts_at: string;
  ends_at: string;
  // Step 3
  issuance_type: IssuanceType;
  shareable_code: string;
  referral_milestone: ReferralMilestone;
  status: 'draft' | 'active';
}

const SOURCE_OPTIONS: { value: Source; label: string; description: string }[] = [
  { value: 'tester', label: 'Tester', description: 'QA / internal tester comps' },
  { value: 'marketing', label: 'Marketing', description: 'Public campaigns, codes' },
  { value: 'referral', label: 'Referral', description: 'Referral payouts' },
  { value: 'support', label: 'Support', description: 'Customer-support comps' },
  { value: 'vip', label: 'VIP', description: 'High-value account perks' },
  { value: 'other', label: 'Other', description: 'Anything else' },
];

const ISSUANCE_OPTIONS: { value: IssuanceType; label: string; description: string }[] = [
  { value: 'admin_grant', label: 'Admin grant only', description: 'Manually grant from User Detail. No code, no auto.' },
  { value: 'shareable_code', label: 'Shareable code', description: 'One code, many users redeem (caps enforced).' },
  { value: 'unique_codes', label: 'Unique single-use codes', description: 'Bulk-generate N codes, each one-use.' },
  { value: 'referral_auto', label: 'Auto-issue on referral milestone', description: 'When referee hits a milestone, both sides get a coupon.' },
];

const MILESTONE_OPTIONS: ReferralMilestone[] = ['signup', 'profile_complete', 'first_match', 'paid'];

export function PromotionCreatePage() {
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState<FormState>({
    name: '',
    description: '',
    source: 'tester',
    paywall_type: 'connect',
    coupons_per_claim: 1,
    max_claims_per_user: 1,
    max_total_claims: '',
    starts_at: '',
    ends_at: '',
    issuance_type: 'admin_grant',
    shareable_code: '',
    referral_milestone: 'paid',
    status: 'draft',
  });

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const step1Valid = form.name.trim().length > 0 && form.name.length <= 100 && Boolean(form.paywall_type);
  const step2Valid = form.coupons_per_claim > 0 && form.max_claims_per_user >= 1;
  const step3Valid =
    form.issuance_type !== 'shareable_code' ||
    (form.shareable_code.trim().length > 0 && /^[A-Z0-9]{1,32}$/.test(form.shareable_code));

  const handleSubmit = async () => {
    if (!step1Valid || !step2Valid || !step3Valid) return;
    setSubmitting(true);
    try {
      const idToken = await getIdToken();
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        source: form.source,
        paywall_type: form.paywall_type,
        coupons_per_claim: form.coupons_per_claim,
        max_claims_per_user: form.max_claims_per_user,
        issuance_type: form.issuance_type,
        status: form.status,
      };
      if (form.description.trim()) body.description = form.description.trim();
      if (form.max_total_claims !== '') body.max_total_claims = Number(form.max_total_claims);
      if (form.starts_at) body.starts_at = new Date(form.starts_at).toISOString();
      if (form.ends_at) body.ends_at = new Date(form.ends_at).toISOString();
      if (form.issuance_type === 'shareable_code') body.shareable_code = form.shareable_code.trim().toUpperCase();
      if (form.issuance_type === 'referral_auto') body.referral_milestone = form.referral_milestone;

      const r = await fetch(`${apiUrl}/v1/admin/promotions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({ message: 'Failed' }));
        throw new Error((err as { message?: string }).message ?? `HTTP ${r.status}`);
      }

      const created = await r.json() as { id: string; name: string; status: string };
      toast({ title: 'Promotion created', description: `${created.name} (${created.status})` });
      navigate(`/promotions/${created.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast({ title: 'Create failed', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/promotions')}
          className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1"
        >
          <ChevronLeft className="w-3 h-3" /> Back to promotions
        </button>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Gift className="w-6 h-6" /> New Promotion
        </h1>
      </div>

      {/* Step indicator */}
      <div className="flex items-center mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center flex-1">
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                  s < step
                    ? 'bg-green-100 text-green-700'
                    : s === step
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              <span className="text-sm whitespace-nowrap">
                {s === 1 && 'Identity'}
                {s === 2 && 'Coupons & limits'}
                {s === 3 && 'Issuance'}
              </span>
            </div>
            {s < 3 && (
              <div className={`flex-1 h-0.5 mx-3 ${s < step ? 'bg-green-300' : 'bg-muted'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Identity */}
      {step === 1 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1">
              Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              maxLength={100}
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g. Welcome Pack 2026"
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">{form.name.length}/100. Internal admin name.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Internal admin notes — never shown to users"
              className="w-full px-3 py-2 border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Source <span className="text-destructive">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SOURCE_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => update('source', o.value)}
                  className={`text-left p-3 border rounded-md transition-colors ${
                    form.source === o.value
                      ? 'border-primary bg-primary/5'
                      : 'border-input hover:bg-muted/50'
                  }`}
                >
                  <div className="font-medium text-sm">{o.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{o.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Paywall <span className="text-destructive">*</span>
            </label>
            <select
              value={form.paywall_type}
              onChange={(e) => update('paywall_type', e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="connect">Match Connection Unlock — $25</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Today only <code className="text-xs">connect</code> is available. New paywalls = 1 INSERT in{' '}
              <code className="text-xs">core.paywall_types</code>.
            </p>
          </div>
        </div>
      )}

      {/* Step 2: Coupons & limits */}
      {step === 2 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1">
              Coupons per claim <span className="text-destructive">*</span>
            </label>
            <input
              type="number"
              min={1}
              value={form.coupons_per_claim}
              onChange={(e) => update('coupons_per_claim', Math.max(1, Number(e.target.value) || 1))}
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">How many free unlocks each claimant gets.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Max claims per user</label>
            <input
              type="number"
              min={1}
              value={form.max_claims_per_user}
              onChange={(e) => update('max_claims_per_user', Math.max(1, Number(e.target.value) || 1))}
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">
              How many times the same user can claim this promotion. Default: 1.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Max total claims</label>
            <input
              type="number"
              min={1}
              value={form.max_total_claims}
              onChange={(e) =>
                update('max_total_claims', e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))
              }
              placeholder="Unlimited"
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">Hard cap across all users. Leave blank for unlimited.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Starts at</label>
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => update('starts_at', e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <p className="text-xs text-muted-foreground mt-1">Default: now.</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Ends at</label>
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => update('ends_at', e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <p className="text-xs text-muted-foreground mt-1">Coupons expire here. Leave blank for no expiry.</p>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Issuance */}
      {step === 3 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-2">
              How is this issued? <span className="text-destructive">*</span>
            </label>
            <div className="space-y-2">
              {ISSUANCE_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => update('issuance_type', o.value)}
                  className={`w-full text-left p-3 border rounded-md transition-colors ${
                    form.issuance_type === o.value
                      ? 'border-primary bg-primary/5'
                      : 'border-input hover:bg-muted/50'
                  }`}
                >
                  <div className="font-medium text-sm">{o.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{o.description}</div>
                </button>
              ))}
            </div>
          </div>

          {form.issuance_type === 'shareable_code' && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Shareable code <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                maxLength={32}
                value={form.shareable_code}
                onChange={(e) => update('shareable_code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="BLACKFRIDAY"
                className="w-full px-3 py-2 border rounded-md bg-background uppercase font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Uppercase alphanumeric, ≤ 32 chars. Many users redeem the same code (subject to caps).
              </p>
              {form.shareable_code.length > 0 && !/^[A-Z0-9]{1,32}$/.test(form.shareable_code) && (
                <p className="text-xs text-destructive mt-1">Only uppercase letters and digits allowed.</p>
              )}
            </div>
          )}

          {form.issuance_type === 'unique_codes' && (
            <div className="p-3 bg-muted/50 rounded-md text-sm text-muted-foreground">
              Generate codes after creating the promotion (Codes tab on the promotion detail page).
            </div>
          )}

          {form.issuance_type === 'referral_auto' && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Referral milestone <span className="text-destructive">*</span>
              </label>
              <select
                value={form.referral_milestone}
                onChange={(e) => update('referral_milestone', e.target.value as ReferralMilestone)}
                className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {MILESTONE_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                When the referee hits this milestone, both sides get coupons. Side-effect lands in Phase 5.
              </p>
            </div>
          )}

          <div className="border-t pt-5">
            <label className="block text-sm font-medium mb-2">Activation</label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => update('status', 'draft')}
                className={`w-full text-left p-3 border rounded-md transition-colors ${
                  form.status === 'draft' ? 'border-primary bg-primary/5' : 'border-input hover:bg-muted/50'
                }`}
              >
                <div className="font-medium text-sm">Save as Draft</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Tweak later. No claims/redemptions until activated.
                </div>
              </button>
              <button
                type="button"
                onClick={() => update('status', 'active')}
                className={`w-full text-left p-3 border rounded-md transition-colors ${
                  form.status === 'active' ? 'border-primary bg-primary/5' : 'border-input hover:bg-muted/50'
                }`}
              >
                <div className="font-medium text-sm">Save &amp; Activate</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Live immediately. Users can claim per the issuance rule.
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t">
        <button
          type="button"
          onClick={() => (step > 1 ? setStep(step - 1) : navigate('/promotions'))}
          disabled={submitting}
          className="inline-flex items-center gap-1 px-4 py-2 border rounded-md text-sm hover:bg-muted/50 disabled:opacity-50"
        >
          <ChevronLeft className="w-4 h-4" />
          {step === 1 ? 'Cancel' : 'Back'}
        </button>

        {step < 3 ? (
          <button
            type="button"
            disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
            onClick={() => setStep(step + 1)}
            className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            disabled={!step1Valid || !step2Valid || !step3Valid || submitting}
            onClick={handleSubmit}
            className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating…' : 'Create Promotion'}
            <Check className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
